import { useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import i18n from 'i18next'
import { useUser } from '../auth'
import { useAppContext } from '../context/AppContext'
import { isItAccess } from '../constants/Roles'
import { getById } from '../server/server'
import { checkIfTeacherHasPrimaryClasses, checkIfTeacherHasRegularClasses } from '../server/usersData'

// Post-login handling shared by password and SSO login. `data` is the /api/login response.
// Returns true when the user is being sent to the student app instead.
export const useCompleteLogin = () => {
    const history = useHistory()
    const { appState, setAppState } = useAppContext()
    const { setUser } = useUser()

    const loadInstitution = async (user) => {
        const resp = await getById({ collection: 'institutions', id: user.institution_id })
        if (resp.status === 200) {
            if (!!resp?.data?.locale) {
                i18n.changeLanguage(resp?.data?.locale)
                localStorage.setItem('locale', resp?.data?.locale)
                setAppState((prev) => ({ ...prev, locale: resp?.data?.locale }))
            }
        }
    }

    return useCallback(
        async (data) => {
            if (data.role === 0) {
                const token = data['token']
                window.open(`https://student.upstrivesystem.com?token=${token}&locale=${data.locale ?? 'en'}`, '_self')
                return true
            }
            localStorage.setItem('token', data['token'])
            localStorage.setItem('loginDate', Date.now())
            localStorage.setItem('user', JSON.stringify(data))
            localStorage.setItem('institution_id', data['institution_id'])
            const user = data
            if (user.new_password_required) {
                // Navigate to set new password screen with token
                history.push(`/auth/set-password`)
            } else if (isItAccess(user)) {
                // IT Access only manages users and has no classes; skip the
                // primary/regular class checks and land on User Management.
                setUser(user)
                localStorage.setItem('uiMode', 'regular')
                setAppState({ ...appState, primary: false })
                history.push('/management')
            } else {
                const hasPrimaryClasses = await checkIfTeacherHasPrimaryClasses()
                if (hasPrimaryClasses) {
                    const hasRegularClasses = await checkIfTeacherHasRegularClasses()
                    setUser({ ...user, hasPrimaryClasses, hasRegularClasses, hasOnlyPrimaryStudents: !hasRegularClasses })
                    const primary = !hasRegularClasses
                    localStorage.setItem('uiMode', primary ? 'primary' : 'regular')
                    setAppState({ ...appState, primary })
                } else {
                    setUser(user)
                    localStorage.setItem('uiMode', 'regular')
                    setAppState({ ...appState, primary: false })
                }
                history.push('/dashboard')
            }
            loadInstitution(data)
            return false
        },
        [history, appState, setAppState, setUser]
    )
}
